/**
 * Data layer for `deterministic-ctv-capi-s2s-for-ctv-conversions`. Thin typed fetch wrappers over /api/v1/deterministic-ctv-capi-s2s-for-ctv-conversions.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DeterministicCtvCapiS2sForCtvConversionsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListDeterministicCtvCapiS2sForCtvConversionsResponse {
	count: number;
	items: DeterministicCtvCapiS2sForCtvConversionsRow[];
}

export interface CreateDeterministicCtvCapiS2sForCtvConversionsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/deterministic-ctv-capi-s2s-for-ctv-conversions";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListDeterministicCtvCapiS2sForCtvConversionsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDeterministicCtvCapiS2sForCtvConversionsResponse>(res);
}

export async function create(body: CreateDeterministicCtvCapiS2sForCtvConversionsBody): Promise<DeterministicCtvCapiS2sForCtvConversionsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<DeterministicCtvCapiS2sForCtvConversionsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const deterministicCtvCapiS2sForCtvConversionsQueryKeys = {
	all: ["deterministic-ctv-capi-s2s-for-ctv-conversions"] as const,
	list: () => [...deterministicCtvCapiS2sForCtvConversionsQueryKeys.all] as const,
	detail: (id: string) => [...deterministicCtvCapiS2sForCtvConversionsQueryKeys.all, id] as const,
};
